import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { updateProfile, changePassword } from "../api/auth";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    recovery_email: "",
  });

  const [pw, setPw] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        username: user.username || "",
        email: user.email || "",
        phone: user.phone || "",
        recovery_email: user.recovery_email || "",
      });
    }
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPwField = (k, v) => setPw((p) => ({ ...p, [k]: v }));

  const profileMut = useMutation({
    mutationFn: (payload) => updateProfile(payload),
    onSuccess: async () => {
      toast("Profile updated", "success");
      await refresh?.();
    },
    onError: (err) =>
      toast(err.response?.data?.message || "Failed to update profile", "error"),
  });

  const pwMut = useMutation({
    mutationFn: (payload) =>
      changePassword(payload.current_password, payload.new_password),
    onSuccess: () => {
      toast("Password changed", "success");
      setPw({ current_password: "", new_password: "", confirm_password: "" });
    },
    onError: (err) =>
      toast(err.response?.data?.message || "Failed to change password", "error"),
  });

  const handleSaveProfile = (e) => {
    e.preventDefault();
    // Convert empty strings to null for optional email fields to avoid Pydantic EmailStr validation errors
    const payload = {
      name: form.name || null,
      username: form.username || null,
      email: form.email,
      phone: form.phone || null,
      recovery_email: form.recovery_email || null,
    };
    profileMut.mutate(payload);
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) {
      toast("New password must be at least 8 characters", "error");
      return;
    }
    if (pw.new_password !== pw.confirm_password) {
      toast("Passwords do not match", "error");
      return;
    }
    pwMut.mutate(pw);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col">
      {/* Header — matches Settings page style */}
      <div className="mx-auto w-[75%] max-w-2xl w-full pt-10 pb-3 flex items-center gap-3 flex-shrink-0">
        <button
          onClick={() => navigate("/")}
          className="text-dark-muted hover:text-dark-text transition-colors"
        >
          <ion-icon name="arrow-back-outline" style={{ fontSize: "20px" }} />
        </button>
        <h1 className="text-base font-semibold text-dark-text">Profile</h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-[75%] max-w-2xl px-0 py-8 space-y-10">

          {/* Avatar + name */}
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 flex-shrink-0 rounded-full bg-[#e5e5e5] flex items-center justify-center">
              <span className="text-base font-semibold text-[#1a1a1a]">
                {(form.name || form.email || "U")[0].toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-dark-text">
                {form.name || form.username || form.email}
              </p>
              <p className="text-xs text-dark-muted capitalize">{user.role}</p>
            </div>
          </div>

          {/* Account details */}
          <section className="space-y-4">
            <h2 className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
              Account details
            </h2>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <Field
                label="Full name"
                value={form.name}
                onChange={(v) => set("name", v)}
                placeholder="Your full name"
              />
              <Field
                label="Username"
                value={form.username}
                onChange={(v) => set("username", v)}
                placeholder="username"
              />
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => set("email", v)}
                required
              />
              <Field
                label="Phone"
                value={form.phone}
                onChange={(v) => set("phone", v)}
                placeholder="+1 555 000 0000"
              />
              <Field
                label="Recovery email (Gmail)"
                type="email"
                value={form.recovery_email}
                onChange={(v) => set("recovery_email", v)}
                placeholder="you@gmail.com"
                hint="OTP for password reset is sent here. Falls back to account email if blank."
              />
              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={profileMut.isPending}
                  className="rounded-full bg-accent px-6 py-2 text-sm font-semibold text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
                >
                  {profileMut.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </section>

          {/* Change password */}
          <section className="space-y-4">
            <h2 className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
              Change password
            </h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <Field
                label="Current password"
                type="password"
                value={pw.current_password}
                onChange={(v) => setPwField("current_password", v)}
                required
              />
              <Field
                label="New password"
                type="password"
                value={pw.new_password}
                onChange={(v) => setPwField("new_password", v)}
                required
              />
              <Field
                label="Confirm new password"
                type="password"
                value={pw.confirm_password}
                onChange={(v) => setPwField("confirm_password", v)}
                required
              />
              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={pwMut.isPending}
                  className="rounded-full bg-accent px-6 py-2 text-sm font-semibold text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
                >
                  {pwMut.isPending ? "Updating…" : "Update password"}
                </button>
              </div>
            </form>
          </section>

        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, hint }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs text-dark-muted">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-lg bg-[#232323] border border-dark-border px-3 py-2 text-sm text-dark-text outline-none focus:border-[#3a3a3a]"
      />
      {hint && <p className="mt-1 text-[11px] text-dark-muted">{hint}</p>}
    </div>
  );
}
